// Hideo - the guided tour (steps and geometry: src/features/tour.ts; drawing: src/components/TourOverlay.vue).
// start() opens it on the first step; go(i) shows a step: it opens the step's page, draws the bubble, then (once the page
// is drawn) scrolls the element into view and places the frame and the bubble on it. A Vue page that is loaded only
// when the tour opens it places them again when it appears (PageView.vue, refit). end() closes it and remembers on this
// device that it was seen. Bridged to the legacy code as startTour, endTour, maybeStartTour.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useDataStore } from './data'
import { useDialogsStore } from './dialogs'
import { useUiStore } from './ui'
import { useIdeaStore } from './idea'
import { TOUR_KEY, TOUR_STEPS, clampStep, spotRect, spotStyle, stepTarget, tipPos, tourKey } from '../features/tour'
import { app } from '../features/app'

export { isPhone } from '../utils/media'
import { isPhone } from '../utils/media'

export const useTourStore = defineStore('tour', () => {
  /** The tour's layer was drawn once (it then stays in the page, hidden, like the legacy one) */
  const started = ref(false)
  /** The tour is on screen */
  const active = ref(false)
  /** Step shown */
  const i = ref(0)
  /** Bumped at every step shown: the bubble's content is drawn again and its entrance animation starts again */
  const seq = ref(0)
  /** Phone layout when the step was shown (its text) */
  const phone = ref(false)
  const step = computed(() => TOUR_STEPS[i.value])
  const total = TOUR_STEPS.length

  function start() {
    if (!useDataStore().currentProfile() || useDialogsStore().isOpen('picker')) return
    useUiStore().closeMobileMenu()
    started.value = true
    active.value = true
    i.value = 0
    go(0)
  }

  function go(n: number) {
    if (!active.value) return
    i.value = clampStep(n)
    const st = TOUR_STEPS[i.value]
    if (st.tab && app.page !== st.tab) app.switchTab(st.tab)
    phone.value = isPhone()
    seq.value++
    // Wait for the page to be drawn, then scroll the element into view and place the frame and the bubble
    requestAnimationFrame(() => {
      const el = target()
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      requestAnimationFrame(place)
    })
  }

  /** The element the current step points at, if it is on screen */
  function target(): HTMLElement | null {
    const sel = stepTarget(TOUR_STEPS[i.value], isPhone())
    const el = sel ? document.querySelector<HTMLElement>(sel) : null
    return el && el.getClientRects().length ? el : null
  }

  /** Places the frame on the current step's element (a point in the centre without one) and the bubble next to it */
  function place() {
    if (!active.value) return
    const spot = document.getElementById('tourSpot'), tip = document.getElementById('tourTip')
    if (!spot || !tip) return
    const el = target(), vw = window.innerWidth, vh = window.innerHeight
    const r = el ? spotRect(el.getBoundingClientRect(), vw, vh) : null
    Object.assign(spot.style, spotStyle(r, vw, vh))
    spot.classList.toggle('tour-spot-on', !!r)
    const p = tipPos(r, tip.offsetWidth, tip.offsetHeight, vw, vh)
    Object.assign(tip.style, { left: p.left + 'px', top: p.top + 'px' })
  }

  /** A Vue page appeared while the tour is on (its code was loaded when the tour opened it): like go, the element is
   *  scrolled into view, then framed (PageView.vue, two frames after it is mounted) */
  function refit() {
    if (!active.value) return
    target()?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    place()
  }

  /** End or skip of the tour: it no longer starts by itself on this device. goHome (last step): the Home, with the
   *  cursor in "Drop your idea here" on a computer */
  function end(goHome?: boolean) {
    active.value = false
    try { localStorage.setItem(TOUR_KEY, 'done') } catch { /* storage unavailable */ }
    if (goHome) { app.switchTab('home'); if (!isPhone()) useIdeaStore().focusText() }
  }

  /** After the profile choice: the tour the first time on this device */
  function maybeStart() {
    let seen = false
    try { seen = localStorage.getItem(TOUR_KEY) === 'done' } catch { /* storage unavailable */ }
    if (!seen) setTimeout(start, 450)
  }

  /** Keyboard during the tour, before every other shortcut (capture-phase listener of TourOverlay.vue): arrows to
   *  move, Enter forward, Esc closes; these keys go no further */
  function keydown(e: KeyboardEvent) {
    if (!active.value) return
    const k = tourKey(e.key, i.value, total)
    if (k === null) return
    if (k === 'skip') end()
    else if (k === 'finish') end(true)
    else go(k)
    e.preventDefault()
    e.stopImmediatePropagation()
  }

  return { started, active, i, seq, phone, step, total, start, go, target, place, refit, end, maybeStart, keydown }
})

/** "Replay the guided tour" / "Take the guided tour" buttons (How it works, Settings › Info, First steps) */
export const startTour = () => useTourStore().start()
